import { PerspectiveCamera } from '@react-three/drei'
import { useFrame, useThree } from '@react-three/fiber'
import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { AssetVizMode } from 'features/assetViz/index'
import { MouseHandler } from 'features/assetViz/systems/MouseHandler'
import {
  CameraBounds,
  CameraCoords,
  Vec2,
  Vec3,
} from 'features/assetViz/utils/helpers'
import { RenderLayerID } from 'features/assetViz/utils/renderingDefines'
import React from 'react'
import * as THREE from 'three'
import { GeckoComponentRect, GeckoMeasurementUnit } from 'types'

const FAR_CLIP = 12 * 10 * 1000
const NEAR_CLIP = 0.25

//------------------------------------------------------------------------------

export type SelectionCallback = (
  componentID: string
) => GeckoComponentRect | undefined
export type BoundsCalculator = (componentID: string) => CameraBounds | undefined
export type PositionCalculator = (cameraCoords: CameraCoords) => {
  position: Vec3
  rotation: Vec3
  smoothMovement?: boolean
}
export type MovementCalculator = (cursorDelta: Vec2) => Vec2
export type FitCalculator = (dimensions: GeckoComponentRect) => Vec2

const vector3 = new THREE.Vector3()
const euler = new THREE.Euler()
const quaternion = new THREE.Quaternion()

//------------------------------------------------------------------------------

/**
 * A camera which looks directly at (i.e. normal to) a single component's
 * surface, used as the default in most scenes. This camera can be easily
 * configured via callbacks to match any desired geometry, and preserves its
 * state across scenes (for example, when switching back and forth between
 * 2D and 3D views)
 * @param onComponentSelected       A callback for when a new component is selected
 * @param getCameraBounds           A callback to limit camera movement
 * @param getCameraPosition         A callback to calculate camera position
 * @param getMovementForCursorDelta An optional callback to determine how much
 *                                  to move the camera (in surface coordinates)
 *                                  given mouse movement
 * @param getCameraDimensionsFit    An optional callback to calculate how wide
 *                                  and tall of a surface bounding box to try
 *                                  and fit on-screen. If none is provided, we'll
 *                                  fit the whole thing, just like a plane
 */
export const DefaultCamera: React.FC<{
  onComponentSelected: SelectionCallback
  getCameraBounds: BoundsCalculator
  getCameraPosition: PositionCalculator
  getMovementForCursorDelta?: MovementCalculator
  getCameraDimensionsFit?: FitCalculator
}> = ({
  onComponentSelected,
  getCameraBounds,
  getCameraPosition,
  getMovementForCursorDelta,
  getCameraDimensionsFit,
}) => {
  //----------------------------------------------------------------------------

  const {
    focusedComponentID,
    setController,
    userIsNavigating,
    viewportSizeDuringAnimation,
    mode,
    overrideCameraAspectRatio,
    cameraFitMultiplier,
  } = useAssetVizInternal()

  // Camera coordinates described in terms of component x and y which persist
  // across different scenes. Note that we don't want to call overrideCameraCoords()
  // very often, it's better to update this object in place since we're accessing
  // this object each frame, rather than on React's refresh cycle
  const [cameraCoords, overrideCameraCoords] = React.useState<CameraCoords>()

  const ref = React.useRef<THREE.PerspectiveCamera>(null)
  const speedRef = React.useRef(0.0)
  const snapToNewPositionRef = React.useRef(false)
  const [hasInitialized, setHasInitialized] = React.useState(false)

  // Component bounds fitting --------------------------------------------------

  const getDistToFit = React.useCallback(
    (rect: GeckoComponentRect): number | undefined => {
      if (!ref.current) return undefined
      const verticalFOV = THREE.MathUtils.degToRad(ref.current.fov)
      const horizontalFOV = ref.current.aspect * verticalFOV
      const [width, height] = !getCameraDimensionsFit
        ? [rect.width.num, rect.height.num]
        : getCameraDimensionsFit(rect)
      const distToFitWidth = (0.5 * width) / Math.tan(0.5 * horizontalFOV)
      const distToFitHeight = (0.5 * height) / Math.tan(0.5 * verticalFOV)
      return (
        Math.max(distToFitWidth, distToFitHeight) * (cameraFitMultiplier ?? 1.3)
      )
    },
    [getCameraDimensionsFit, cameraFitMultiplier]
  )

  const bounds = React.useMemo<CameraBounds | undefined>(() => {
    if (!cameraCoords || !hasInitialized) return undefined

    const newBounds = getCameraBounds(cameraCoords.componentID)
    if (newBounds) {
      if (!newBounds.distToComponentMin)
        newBounds.distToComponentMin = NEAR_CLIP
      if (!newBounds.distToComponentMax)
        newBounds.distToComponentMax = getDistToFit(newBounds.rect)
      return newBounds
    }

    return {
      rect: GeckoComponentRect.CreateFromXYs(
        '',
        Number.NEGATIVE_INFINITY,
        Number.NEGATIVE_INFINITY,
        Number.POSITIVE_INFINITY,
        Number.POSITIVE_INFINITY,
        GeckoMeasurementUnit.Feet,
        GeckoMeasurementUnit.Feet
      ),
      distToComponentMin: 6.0,
      distToComponentMax: Number.POSITIVE_INFINITY,
    }
  }, [cameraCoords, hasInitialized, getCameraBounds, getDistToFit])

  const wrapX = React.useCallback(
    (x: number) => {
      if (!bounds || !bounds.doWrapX) return x
      const { start, end, width } = bounds.rect
      if (x === end.x.num) return x
      return (
        THREE.MathUtils.euclideanModulo(x - start.x.num, width.num) +
        start.x.num
      )
    },
    [bounds]
  )

  const wrapY = React.useCallback(
    (y: number) => {
      if (!bounds || !bounds.doWrapY) return y
      const { start, end, height } = bounds.rect
      if (y === end.y.num) return y
      return (
        THREE.MathUtils.euclideanModulo(y - start.y.num, height.num) +
        start.y.num
      )
    },
    [bounds]
  )

  React.useEffect(() => {
    // If camera coords haven't been set yet, pick a new component to look at
    if (!cameraCoords || cameraCoords.componentID !== focusedComponentID) {
      const newDims = onComponentSelected(focusedComponentID)
      const distToFit = !newDims ? undefined : getDistToFit(newDims)
      if (!!newDims && distToFit !== undefined && !Number.isNaN(distToFit))
        overrideCameraCoords({
          componentID: focusedComponentID,
          componentX: THREE.MathUtils.lerp(
            newDims.start.x.num,
            newDims.end.x.num,
            0.5
          ),
          componentY: THREE.MathUtils.lerp(
            newDims.start.y.num,
            newDims.end.y.num,
            0.5
          ),
          distToComponent: distToFit,
        })
    }
    // Otherwise, if this scene hasn't been initialized yet, initialize it
    else if (!hasInitialized) {
      ref.current?.layers.disableAll()
      ref.current?.layers.enable(RenderLayerID.Default)
      ref.current?.layers.enable(RenderLayerID.VisibleCollidable)
      ref.current?.layers.enable(RenderLayerID.VisibleNonCollidable)
      setHasInitialized(true)
      onComponentSelected(focusedComponentID)
    }
    snapToNewPositionRef.current = true
  }, [
    cameraCoords,
    focusedComponentID,
    getDistToFit,
    hasInitialized,
    onComponentSelected,
  ])

  // zoomTo() logic ------------------------------------------------------------

  const [currentZoom, setCurrentZoom] = React.useState<{
    rect: GeckoComponentRect
    doZoom: boolean
    reject: (reason?: void) => void
    resolve: (value?: void | PromiseLike<void>) => void
  }>()

  // The zoomTarget is based on the currentZoom, but is broken out into its own
  // memoized object to avoid lots of setController() callbacks
  const zoomTarget = React.useMemo<CameraCoords | undefined>(() => {
    if (!bounds || !currentZoom) return undefined

    const xEnd = wrapX(currentZoom.rect.end.x.num)
    const yEnd = wrapY(currentZoom.rect.end.y.num)
    const distToFit = getDistToFit(
      GeckoComponentRect.CreateFromXYs(
        currentZoom.rect.componentID,
        currentZoom.rect.start.x.num,
        currentZoom.rect.start.y.num,
        xEnd,
        yEnd,
        currentZoom.rect.x.unit,
        currentZoom.rect.y.unit
      )
    )
    return !distToFit
      ? undefined
      : {
          componentID: currentZoom.rect.componentID,
          componentX: THREE.MathUtils.lerp(
            currentZoom.rect.start.x.num,
            xEnd,
            0.5
          ),
          componentY: THREE.MathUtils.lerp(
            currentZoom.rect.start.y.num,
            yEnd,
            0.5
          ),
          distToComponent: distToFit,
        }
  }, [bounds, getDistToFit, wrapX, wrapY, currentZoom])

  // spread params so that interactions layer is not overwritten
  React.useEffect(
    () =>
      setController((prev) => ({
        ...prev,
        zoomToFit: (rect) =>
          new Promise<void>((resolve, reject) =>
            setCurrentZoom({
              resolve,
              reject,
              rect: rect.project(bounds!.rect.space),
              doZoom: true,
            })
          ),
        snapToFit: (rect) => {
          const projectedRect = rect.project(bounds!.rect.space)
          const distToFit = getDistToFit(projectedRect)

          if (distToFit !== undefined && cameraCoords) {
            const xEnd = wrapX(projectedRect.end.x.num)
            const yEnd = wrapY(projectedRect.end.y.num)

            // Update camera coordinates
            cameraCoords.componentX = THREE.MathUtils.lerp(
              projectedRect.start.x.num,
              xEnd,
              0.5
            )
            cameraCoords.componentY = THREE.MathUtils.lerp(
              projectedRect.start.y.num,
              yEnd,
              0.5
            )
            cameraCoords.distToComponent = distToFit

            // Force an immediate position update
            snapToNewPositionRef.current = true
          }

          return Promise.resolve()
        },
        centerOn: (rect) =>
          new Promise<void>((resolve, reject) =>
            setCurrentZoom({
              resolve,
              reject,
              rect: rect.project(bounds!.rect.space),
              doZoom: false,
            })
          ),
        cancelZoom: () => {
          setCurrentZoom(undefined)
        },
        resetCamera: () => {
          overrideCameraCoords(undefined)
          return
        },
      })),
    [bounds, cameraCoords, getDistToFit, setController, wrapX, wrapY]
  )

  // Smooth animations during resizing -----------------------------------------

  const { gl } = useThree()

  React.useEffect(() => {
    const canvas = gl.domElement
    const resizeObserver = new ResizeObserver(() => {
      const size = [
        gl.domElement.parentElement!.clientWidth,
        gl.domElement.parentElement!.clientHeight,
      ] as Vec2
      canvas.style.width = size[0] + 'px'
      canvas.style.height = size[1] + 'px'
      if (ref.current) {
        ref.current.aspect = size[0] / size[1]
        ref.current.updateProjectionMatrix()
        if (viewportSizeDuringAnimation.current) {
          viewportSizeDuringAnimation.current[0] = size[0]
          viewportSizeDuringAnimation.current[1] = size[1]
          viewportSizeDuringAnimation.current[2] = 2
        }
      }
    })
    resizeObserver.observe(canvas.parentElement!)

    return () => {
      resizeObserver.unobserve(canvas.parentElement!)
    }
  })

  // Frame loop ----------------------------------------------------------------

  useFrame((_, delta) => {
    if (!cameraCoords || !bounds) return

    if (
      viewportSizeDuringAnimation.current &&
      viewportSizeDuringAnimation.current[2] > 0
    ) {
      viewportSizeDuringAnimation.current[2]--
    }
    if (
      ref.current &&
      overrideCameraAspectRatio &&
      overrideCameraAspectRatio.current !== null &&
      overrideCameraAspectRatio.current! > 0
    ) {
      ref.current.aspect = overrideCameraAspectRatio.current
      ref.current.updateProjectionMatrix()
    }

    // Figure out where the camera should move
    const targetX = wrapX(
      zoomTarget
        ? zoomTarget.componentX
        : bounds.doWrapX
          ? cameraCoords.componentX
          : THREE.MathUtils.clamp(
              cameraCoords.componentX,
              bounds.rect.start.x.num,
              bounds.rect.end.x.num
            )
    )
    const targetY = wrapY(
      zoomTarget
        ? zoomTarget.componentY
        : bounds.doWrapY
          ? cameraCoords.componentY
          : THREE.MathUtils.clamp(
              cameraCoords.componentY,
              bounds.rect.start.y.num,
              bounds.rect.end.y.num
            )
    )
    const targetD =
      !!zoomTarget && !!currentZoom && currentZoom.doZoom
        ? zoomTarget.distToComponent
        : THREE.MathUtils.clamp(
            cameraCoords.distToComponent,
            bounds.distToComponentMin ?? Number.NEGATIVE_INFINITY,
            1.5 * (bounds.distToComponentMax ?? Number.POSITIVE_INFINITY)
          )

    // Figure out the shortest route to the target coordinates and distance-to-
    // component. Note that we might need to wrap around "the other way"
    let deltaX = targetX - cameraCoords.componentX
    if (bounds.doWrapX && Math.abs(deltaX) > bounds.rect.width.num / 2)
      deltaX = -Math.sign(deltaX) * bounds.rect.width.num + deltaX
    let deltaY = targetY - cameraCoords.componentY
    if (bounds.doWrapY && Math.abs(deltaY) > bounds.rect.height.num / 2)
      deltaY = -Math.sign(deltaY) * bounds.rect.height.num + deltaY
    const deltaD = targetD - cameraCoords.distToComponent

    const magnitude = Math.max(
      Number.EPSILON,
      Math.sqrt(deltaX * deltaX + deltaY * deltaY + deltaD * deltaD)
    )
    speedRef.current = userIsNavigating
      ? 0 // Prevents jerky motion when the user's navigating
      : Math.min(
          Math.max(cameraCoords.distToComponent, 12.0) *
            delta *
            Math.min(
              speedRef.current + 2.5, // Acceleration
              25.0 // Max speed
            ),
          0.25 * magnitude // Deceleration
        )

    speedRef.current /= magnitude // Normalize
    const velocityX = speedRef.current * deltaX
    const velocityY = speedRef.current * deltaY
    const velocityD = speedRef.current * deltaD

    cameraCoords.componentX = wrapX(cameraCoords.componentX + velocityX)
    cameraCoords.componentY = wrapY(cameraCoords.componentY + velocityY)
    cameraCoords.distToComponent = THREE.MathUtils.clamp(
      bounds.distToComponentMin ?? NEAR_CLIP,
      cameraCoords.distToComponent + velocityD,
      FAR_CLIP * 0.8
    )

    // zoomTo() handling -------------------------------------------------------

    // Stop zooming if the user starts clicking around or if we reach the target
    if (currentZoom) {
      if (userIsNavigating) {
        currentZoom.reject()
        setCurrentZoom(undefined)
      } else if (Math.abs(velocityX + velocityY + velocityD) < 0.001) {
        currentZoom.resolve()
        setCurrentZoom(undefined)
      }
    }

    // Camera movement ---------------------------------------------------------

    if (ref.current) {
      const { position, rotation, smoothMovement } =
        getCameraPosition(cameraCoords)

      // Smooth movement
      if (smoothMovement && !snapToNewPositionRef.current) {
        vector3.set(
          position[0] - ref.current.position.x,
          position[1] - ref.current.position.y,
          position[2] - ref.current.position.z
        )
        vector3.multiplyScalar(8.0 * delta) // Speed is determined by delta
        ref.current.position.add(vector3)

        euler.set(rotation[0], rotation[1], rotation[2])
        quaternion.setFromEuler(euler)
        ref.current.quaternion.slerp(quaternion, 8.0 * delta) // Speed is determined by delta
      }
      // Snap movement
      else {
        ref.current.position.set(...position)
        euler.set(rotation[0], rotation[1], rotation[2])
        ref.current.setRotationFromEuler(euler)
        snapToNewPositionRef.current = false
      }
    }
  })

  //----------------------------------------------------------------------------

  return (
    <>
      <PerspectiveCamera
        makeDefault
        ref={ref}
        fov={50}
        near={NEAR_CLIP}
        far={FAR_CLIP}
        // Set the starting position way outside the bounds of the scene for
        // the first frame to prevent an awkward first render at [0, 0, 0]
        position={[1e20, 1e20, 1e20]}
      />
      {!cameraCoords || mode === AssetVizMode.NoInteraction ? null : (
        <MouseHandler
          cameraCoords={cameraCoords}
          getMovementForCursorDelta={getMovementForCursorDelta ?? ((xy) => xy)}
        />
      )}
    </>
  )
}
